import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { MonitorPlay, X, Play, Mail, MessageCircle, Linkedin, Facebook, Music2, Copy, Share2, Download, Pencil, Users } from 'lucide-react';
import { PcInstallBox } from './PcInstallBox';
import { promoCardPdf } from '../lib/promoCard';
import { startDemo } from './DemoMode';
import { modeId } from '../lib/profiles';
import SECTOR from '../lib/sectorTours.json';
import { playLinks, refreshContact, signupTester, getContact, isAdmin, saveContact, listTesters, APP_INFO } from '../lib/license';

const enc = encodeURIComponent;

function AdminLinks({ t, onSaved }) {
  const [c, setC] = useState(getContact());
  const [edit, setEdit] = useState(false);
  const [n, setN] = useState(null);
  const [bad, setBad] = useState(false);
  const save = async () => {
    try { await saveContact(c); setBad(false); toast.success(t.contactSaved); setEdit(false); onSaved(); } catch (e) { setBad(e.message === 'play_url'); toast.error(e.message === 'play_url' ? t.shPlayUrlBad : e.message === 'owner' ? t.ownerExpired : t.licOffline); }
  };
  const csv = async () => {
    try {
      const list = await listTesters();
      setN(list.length);
      const rows = [['email', 'created_at', 'lang', 'device_id'], ...list.map((x) => [x.email, x.created_at, x.lang, x.device_id])];
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv' }));
      a.download = 'play-testers.csv'; a.click();
    } catch { toast.error(t.ownerExpired); }
  };
  return (
    <div className="ent-gen" data-testid="share-admin">
      <b>🔧 {t.shAdmin}</b>
      {edit ? (
        <>
          <input type="url" data-testid="share-play-url-input" placeholder="https://play.google.com/store/apps/details?id=…" value={c.play_url || ''} onChange={(e) => setC({ ...c, play_url: e.target.value })} />
          <input type="url" data-testid="share-play-test-url-input" placeholder="https://play.google.com/apps/testing/…" value={c.play_test_url || ''} onChange={(e) => setC({ ...c, play_test_url: e.target.value })} />
          {bad && <small className="sh-err" data-testid="share-play-url-error">{t.shPlayUrlBad}</small>}
          <button className="btn btn-dark btn-sm" data-testid="share-play-url-save-btn" onClick={save}>{t.contactSave}</button>
        </>
      ) : <button className="btn btn-outline btn-sm" data-testid="share-play-url-edit-btn" onClick={() => setEdit(true)}><Pencil size={14} /> {t.shEditLinks}</button>}
      <button className="btn btn-outline btn-sm" data-testid="share-testers-export-btn" onClick={csv}><Users size={14} /> {t.shTestersCsv}{n != null ? ` (${n})` : ''}</button>
    </div>
  );
}

function GmailBox({ t, lang }) {
  const [mail, setMail] = useState('');
  const [sent, setSent] = useState(false);
  const c = getContact();
  const ok = /^[a-z0-9.+_-]{3,64}@(gmail|googlemail)\.com$/i.test(mail.trim());
  const msg = `${t.shGmailMsg} ${mail.trim()} (${APP_INFO.name})`;
  const send = async () => {
    try { await signupTester(mail.trim(), lang); setSent(true); toast.success(t.shGmailOk); } catch (e) { toast.error(e.message === 'gmail' ? t.shGmailBad : t.licOffline); }
  };
  return (
    <div className="sh-gmail" data-testid="share-gmail-box">
      <label htmlFor="share-gmail-input"><b>📧 {t.shGmailTitle}</b></label>
      <div className="row">
        <input id="share-gmail-input" type="email" inputMode="email" data-testid="share-gmail-input" placeholder="name@gmail.com" value={mail} onChange={(e) => { setMail(e.target.value); setSent(false); }} />
        <button className="btn btn-green btn-sm" data-testid="share-gmail-send-btn" disabled={!ok} onClick={send}><Mail size={14} /> {t.shGmailSend}</button>
      </div>
      {mail && !ok && <small className="sh-err" data-testid="share-gmail-error">{t.shGmailBad}</small>}
      {sent && (
        <div className="row" data-testid="share-gmail-notify">
          <a className="btn btn-share btn-sm" data-testid="share-gmail-whatsapp-btn" target="_blank" rel="noreferrer" href={`https://wa.me/${(c.whatsapp || c.phone || '').replace(/\D/g, '')}?text=${enc(msg)}`}><MessageCircle size={14} /> WhatsApp</a>
          {c.email && <a className="btn btn-outline btn-sm" data-testid="share-gmail-email-btn" href={`mailto:${c.email}?subject=${enc(t.shGmailTitle)}&body=${enc(msg)}`}><Mail size={14} /> E-Mail</a>}
        </div>
      )}
    </div>
  );
}

export default function AppShare({ t, lang, onClose }) {
  const [links, setLinks] = useState(playLinks());
  const [qr, setQr] = useState(null);
  useEffect(() => { refreshContact().then(() => setLinks(playLinks())); }, []);
  useEffect(() => { QRCode.toDataURL(links.open, { width: 520, margin: 2, errorCorrectionLevel: 'M' }).then(setQr); }, [links.open]);
  const text = `${t.shPostText} ${links.open}`;
  const copy = async (s, toastMsg) => { try { await navigator.clipboard.writeText(s); toast.success(toastMsg || t.shCopied); } catch { window.prompt('', s); } };
  const nat = () => navigator.share?.({ title: APP_INFO.name, text: t.shPostText, url: links.open }).catch(() => {});
  return createPortal(
    <div className="editor-backdrop" data-testid="share-dialog" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="editor sh-dlg">
        <div className="editor-head">
          <h4>📲 {t.appShareTitle}</h4>
          <button className="icon-btn" data-testid="share-close-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sh-play" data-testid="share-play-box">
          <b>🚀 {t.shPlayTitle}</b>
          <p className="muted small-hint">{t.shPlayHint}</p>
          {qr && <img className="sh-qr" src={qr} alt="QR" data-testid="share-qr-img" />}
          <a className="btn btn-play" data-testid="share-open-play-btn" href={links.open} target="_blank" rel="noreferrer"><Play size={16} fill="currentColor" /> {t.shOpenPlay}</a>
          <small className="mono sh-link" data-testid="share-play-link">{links.open}</small>
          {qr && <a className="btn btn-outline btn-sm" data-testid="share-qr-download-btn" href={qr} download="giss-play-qr.png"><Download size={14} /> {t.shQrPng}</a>}
          <button className="btn btn-proj btn-sm" data-testid="share-promo-card-btn" onClick={() => { const c = getContact() || {}; const sec = (SECTOR[lang] || SECTOR.de)[modeId()]; promoCardPdf({ t, url: links.open, feats: sec && sec.slice(0, 3).map(([a, b]) => `${a} – ${b}`), contact: [c.name, c.phone, c.email, c.web].filter(Boolean).join(' · ') }); }}><Download size={14} /> {t.pcBtn}</button>
          <button className="btn btn-primary btn-sm" data-testid="share-demo-btn" onClick={() => { onClose(); setTimeout(startDemo, 300); }}><MonitorPlay size={14} /> {t.demoStart}</button>
        </div>
        <GmailBox t={t} lang={lang} />
        <PcInstallBox t={t} />
        <div className="sh-social" data-testid="share-social">
          <b>{t.shSocial}</b>
          <div className="row">
            <a className="btn btn-share btn-sm" data-testid="share-whatsapp-btn" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${enc(text)}`}><MessageCircle size={14} /> WhatsApp</a>
            <a className="btn btn-sm sh-li" data-testid="share-linkedin-btn" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(links.open)}`}><Linkedin size={14} /> LinkedIn</a>
            <a className="btn btn-sm sh-fb" data-testid="share-facebook-btn" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(links.open)}`}><Facebook size={14} /> Facebook</a>
            <button className="btn btn-sm sh-tt" data-testid="share-tiktok-btn" onClick={() => { copy(text, t.shTiktokCopied); window.open('https://www.tiktok.com/upload', '_blank', 'noopener'); }}><Music2 size={14} /> TikTok</button>
            <button className="btn btn-outline btn-sm" data-testid="share-copy-btn" onClick={() => copy(text)}><Copy size={14} /> {t.shCopy}</button>
            {navigator.share && <button className="btn btn-outline btn-sm" data-testid="share-native-btn" onClick={nat}><Share2 size={14} /> {t.shMore}</button>}
          </div>
        </div>
        {isAdmin() && <AdminLinks t={t} onSaved={() => setLinks(playLinks())} />}
      </div>
    </div>,
    document.body,
  );
}
